import React, { useEffect, useMemo, useState } from 'react';
import DynamicField from './DynamicField';
import MemberPicker from './MemberPicker';
import { StatusSelect } from './StatusBadge';
import { trackApi, errMsg, getViewerId } from '../../lib/trackApi';
import { btn, btnGhost, input as inputStyle, select as selectStyle, labelStyle, C, todayYmd } from './trackUi';

function Row({ label, required, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}{required ? <span style={{ color: C.danger }}> *</span> : null}</label>
      {children}
    </div>
  );
}

/**
 * 新增任務／子任務表單：固定欄位＋看板自訂欄位
 */
export default function TaskCreateForm({ boards, members, boardId, parent, defaultAssigneeId, onCreated, onCancel }) {
  const fixedBoard = parent ? parent.boardId : boardId;
  const [selBoard, setSelBoard] = useState(fixedBoard || (boards || []).find((b) => b.isActive)?.id || null);
  const board = useMemo(() => (boards || []).find((b) => b.id === Number(selBoard)), [boards, selBoard]);
  const [fields, setFields] = useState([]);
  const viewer = Number(getViewerId()) || null;
  const [draft, setDraft] = useState(() => ({
    title: '',
    content: '',
    initiatorId: viewer,
    assigneeId: defaultAssigneeId || (parent ? parent.assigneeId : null),
    status: '',
    startDate: todayYmd(),
    dueDate: parent?.dueDate || '',
    note: '',
    extra: {},
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selBoard || parent) { setFields([]); return; }
    trackApi.listFields(selBoard).then((d) => setFields(d?.data || [])).catch(() => setFields([]));
  }, [selBoard, parent]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setExtra = (key, v) => setDraft((d) => ({ ...d, extra: { ...d.extra, [key]: v } }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!draft.title.trim()) { setError('請填寫任務名稱'); return; }
    const missing = fields.filter((f) => f.isRequired && (draft.extra[f.key] == null || draft.extra[f.key] === '' || (Array.isArray(draft.extra[f.key]) && !draft.extra[f.key].length)));
    if (missing.length) { setError(`必填：${missing.map((f) => f.label).join('、')}`); return; }
    setBusy(true);
    setError('');
    try {
      const data = await trackApi.createTask({
        ...draft,
        boardId: selBoard,
        parentId: parent?.id || null,
        status: draft.status || undefined,
      });
      if (!data?.ok) throw new Error(data?.error || '新增失敗');
      onCreated?.(data.data);
      setDraft((d) => ({ ...d, title: '', content: '', note: '', extra: {} }));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const half = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0 12px' };

  return (
    <form onSubmit={submit} style={{ background: '#fafafa', border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>
        {parent ? `新增子任務（${parent.taskNo} ${parent.title}）` : '新增任務'}
      </div>
      {!fixedBoard && (
        <Row label="看板" required>
          <select style={{ ...selectStyle, width: '100%' }} value={selBoard || ''} onChange={(e) => setSelBoard(Number(e.target.value) || null)}>
            {(boards || []).filter((b) => b.isActive).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </Row>
      )}
      <Row label={parent ? '子任務名稱' : '任務名稱'} required>
        <input style={{ ...inputStyle, width: '100%' }} value={draft.title} onChange={(e) => set({ title: e.target.value })} autoFocus />
      </Row>
      <Row label="內容">
        <textarea rows={2} style={{ ...inputStyle, width: '100%' }} value={draft.content} onChange={(e) => set({ content: e.target.value })} />
      </Row>
      <div style={half}>
        <Row label="發起人">
          <MemberPicker members={members} value={draft.initiatorId} onChange={(v) => set({ initiatorId: v })} style={{ width: '100%' }} />
        </Row>
        <Row label="執行人">
          <MemberPicker members={members} value={draft.assigneeId} onChange={(v) => set({ assigneeId: v })} style={{ width: '100%' }} />
        </Row>
        <Row label="狀態">
          <StatusSelect
            statuses={board?.statuses || []}
            value={draft.status || board?.statuses?.find((s) => !s.isDone)?.key}
            onChange={(v) => set({ status: v })}
            style={{ width: '100%' }}
          />
        </Row>
        <Row label="開始時間">
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={draft.startDate || ''} onChange={(e) => set({ startDate: e.target.value })} />
        </Row>
        <Row label="預計完成時間">
          <input type="date" style={{ ...inputStyle, width: '100%' }} value={draft.dueDate || ''} onChange={(e) => set({ dueDate: e.target.value })} />
        </Row>
      </div>
      {fields.map((f) => (
        <Row key={f.id} label={f.label} required={f.isRequired}>
          <DynamicField field={f} value={draft.extra[f.key]} onChange={(v) => setExtra(f.key, v)} members={members} />
        </Row>
      ))}
      <Row label="備註">
        <textarea rows={2} style={{ ...inputStyle, width: '100%' }} value={draft.note} onChange={(e) => set({ note: e.target.value })} />
      </Row>
      {error && <div style={{ color: C.danger, fontSize: 13, marginBottom: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={btn} disabled={busy}>{busy ? '新增中…' : '新增'}</button>
        {onCancel && <button type="button" style={btnGhost} onClick={onCancel}>取消</button>}
      </div>
    </form>
  );
}
